import { useMutation, useQueryClient } from '@tanstack/react-query';
import z from 'zod';
import { StatusCode, patchApiV2CustomersCustomerId } from '@hungryroot/rest';
import { invalidateAccountSettings } from './useGetAccountSettings';

const MIN_PW_LENGTH = 8;
const MIN_PW_LENGTH_WARNING =
  'Password must be at least 8 characters long and difficult to guess.';
const INVALID_EMAIL_ADDRESS_WARNING = 'Invalid email address.';

export const AccountDetailsSchema = z
  .object({
    email: z
      .string({
        required_error: INVALID_EMAIL_ADDRESS_WARNING,
      })
      .email({
        message: INVALID_EMAIL_ADDRESS_WARNING,
      })
      .trim()
      .toLowerCase(),
    password: z
      .string()
      .min(MIN_PW_LENGTH, {
        message: MIN_PW_LENGTH_WARNING,
      })
      .optional(),
    password_confirm: z
      .string()
      .min(MIN_PW_LENGTH, {
        message: MIN_PW_LENGTH_WARNING,
      })
      .optional(),
  })
  .superRefine(({ password_confirm, password }, ctx) => {
    if (password_confirm !== password) {
      ctx.addIssue({
        path: ['password_confirm'],
        code: 'custom',
        message: 'Password confirmation does not match.',
      });
    }
  });

export type AccountDetailsPayload = z.infer<typeof AccountDetailsSchema>;

export function useUpdateAccountDetails() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: AccountDetailsPayload) => {
      const resp = await patchApiV2CustomersCustomerId({
        customer: payload,
      });

      if (resp.status !== StatusCode.SuccessOK) {
        throw new Error(JSON.stringify(resp.data, null, 2));
      }

      return resp.data;
    },
    onSuccess() {
      invalidateAccountSettings(queryClient);
    },
  });
}
